import InfoIcon from '@mui/icons-material/Info';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { handleAttachmentFile } from '../../stats/utils';
import DragAndDropFile from './DragAndDropFile.jsx';

const Dropzone = ({
  attachments,
  setAttachments,
  deleteAttachment,
  attachmentsSize,
  setTotalAttachmentsSize,
  onFileAdd,
  templateId,
  type,
}) => {
  const [openAlert, setOpenAlert] = useState(false);
  const isDisabled = useMemo(() => {
    return type === 'template' && !templateId;
  }, [type, templateId]);

  const handleOpenAlert = () => {
    setOpenAlert(true);
    setTimeout(handleCloseAlert, 15000);
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  const handleDeleteAttachment = async (attChip) => {
    const attName = attChip?.file?.name || attChip?.name;
    const att = attachments?.find(
      (attachment) =>
        attachment?.file?.name === attName || attachment?.name === attName
    );
    if (att) {
      // Marquer l'attachment comme "loading" sans le retirer de la liste
      setAttachments((prev) =>
        prev.map((attachment) =>
          attachment?.file?.name === attName || attachment?.name === attName
            ? { ...attachment, status: 'loading' }
            : attachment
        )
      );

      try {
        if (deleteAttachment) {
          await deleteAttachment(att);
          // La mise à jour de la liste se fera via le callback de deleteAttachment
        }
      } catch (error) {
        console.error("Erreur lors de la suppression de l'attachment:", error);
        // En cas d'erreur, remettre l'attachment en état normal
        setAttachments((prev) =>
          prev.map((attachment) =>
            attachment?.file?.name === attName || attachment?.name === attName
              ? { ...attachment, status: 'default' }
              : attachment
          )
        );
      }
    }
  };

  const handleAttachmentFiles = async (files) => {
    if (onFileAdd) {
      for (const file of files) {
        if (
          file?.size >
            (Number(import.meta.env.VITE_MAXIMUM_FILE_SIZE) || 5000000) ||
          attachmentsSize + file?.size >
            (Number(import.meta.env.VITE_UM_TOTAL_FILES_SIZE) || 20000000)
        ) {
          handleOpenAlert();
        } else {
          // Créer un attachement temporaire avec statut loading
          const tempAttachment = await handleAttachmentFile(
            file,
            attachments,
            true
          );
          if (tempAttachment) {
            await onFileAdd(tempAttachment);
          }
        }
      }
    } else {
      const filesToAdd = [];
      for (const file of files) {
        if (
          file?.size > (Number(import.meta.env.VITE_UM_FILE_SIZE) || 5000000) ||
          attachmentsSize + file?.size >
            (Number(import.meta.env.VITE_UM_TOTAL_FILES_SIZE) || 20000000)
        ) {
          handleOpenAlert();
        } else {
          const res = await handleAttachmentFile(file, attachments);
          if (res !== undefined) {
            filesToAdd.push({ ...res, status: 'loading' });
          }
        }
      }
      if (filesToAdd?.length > 0) {
        setAttachments((state) => [...state, ...filesToAdd]);
        setTotalAttachmentsSize(
          attachmentsSize +
            filesToAdd.reduce(
              (acc, file) => acc + (file?.file?.size || file?.size),
              0
            )
        );
      }
    }
  };

  const getFileName = (attachment) => {
    const fileName = attachment?.file?.name || attachment?.name;
    if (!fileName) return '';

    const maxLength = 17; // Limite de caractères pour le nom du fichier

    if (fileName.length <= maxLength) return fileName;

    // Sépare le nom du fichier et son extension
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) {
      // Pas d'extension
      return `${fileName.substring(0, maxLength)}...`;
    }

    const name = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex);

    // Garde l'extension et tronque le nom
    const truncatedLength = maxLength - extension.length - 3; // -3 pour "..."
    return `${name.substring(0, truncatedLength)}...${extension}`;
  };

  const getAttachmentLabel = (attachment) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <div>{getFileName(attachment)}</div>
        {attachment?.status === 'loading' && (
          <CircularProgress size={16} sx={{ ml: 1 }} />
        )}
      </Box>
    );
  };

  const isNotDeletable = (attachment) => {
    return (
      attachment?.link?.includes('pandora/logo-hoortrade.jpg') ||
      attachment?.status === 'loading' ||
      !attachment?.isAtt
    );
  };

  const getColor = (attachment) => {
    if (
      isNotDeletable(attachment) &&
      attachment?.link?.includes('pandora/logo-hoortrade.jpg')
    ) {
      return 'default';
    }

    if (attachment?.status === 'loading') {
      return 'default';
    }

    if (!attachment?.isAtt) {
      return 'success';
    }
    return 'primary';
  };

  const filterAttachments = (attachments) => {
    // Fonction utilitaire pour obtenir le nom du fichier (priorité à file.name)
    const getFileName = (attachment) => {
      return attachment?.file?.name || attachment?.name;
    };

    // Fonction utilitaire pour obtenir les propriétés du fichier
    const getFileProperties = (attachment) => {
      return {
        name: getFileName(attachment),
        size: attachment?.file?.size || attachment?.size,
        type:
          attachment?.file?.type || attachment?.type || attachment?.mimeType,
        content: attachment?.content,
        link: attachment?.link,
      };
    };

    // Filtrer les doublons en gardant uniquement la première occurrence
    const uniqueAttachments = attachments?.reduce((acc, current) => {
      const currentProps = getFileProperties(current);
      // Vérifier si un doublon existe déjà
      const isDuplicate = acc.some((existing) => {
        const existingProps = getFileProperties(existing);
        return (
          // Vérification par nom et taille
          (existingProps.name === currentProps.name &&
            existingProps.size === currentProps.size) ||
          // Vérification par contenu
          (currentProps.content &&
            existingProps.content === currentProps.content) ||
          // Vérification par lien
          (currentProps.link && existingProps.link === currentProps.link)
        );
      });

      // Ajouter seulement si ce n'est pas un doublon et si c'est un attachment ou non supprimable
      if (!isDuplicate && (current?.isAtt || isNotDeletable(current))) {
        acc.push(current);
      }

      return acc;
    }, []);

    return uniqueAttachments;
  };

  if (isDisabled) {
    return (
      <Box
        sx={{ width: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <InfoIcon color="primary" />
        <Typography color="primary">
          Pour ajouter des pièces-jointes, veuillez d&apos;abord sauvegarder le
          template.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '4px',
          fullWidth: 'auto',
        }}
      >
        {filterAttachments(attachments)?.map((attachment, idx) => (
          <Tooltip key={idx} title={attachment?.file?.name || attachment?.name}>
            <Chip
              variant="filled"
              color={getColor(attachment)}
              label={getAttachmentLabel(attachment)}
              sx={{ cursor: 'pointer' }}
              onDelete={
                isNotDeletable(attachment)
                  ? undefined
                  : (_e) => handleDeleteAttachment(attachment)
              }
              disabled={!attachment?._id}
            />
          </Tooltip>
        ))}
      </Box>
      {openAlert && (
        <Alert severity="error" onClose={handleCloseAlert}>
          <AlertTitle>Taille de pièce-jointe trop élevée.</AlertTitle>
          La pièce jointe que vous voulez ajouter est trop lourde, veuillez
          réduire sa taille ou passer par un hébergement externe tel que &apos;
          <a href="https://wetransfer.com/" target="_blank" rel="noreferrer">
            WeTransfer
          </a>
          &apos;
          <br />5 Mo par fichier
          <br />
          20 Mo au total
        </Alert>
      )}
      <Box>
        <DragAndDropFile
          handleAttachmentFiles={handleAttachmentFiles}
          size={attachmentsSize}
          handleSizeError={handleOpenAlert}
        />
      </Box>
    </Box>
  );
};

export default Dropzone;
